import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { createComponentLogger } from '../../utils/logger';
import { getErrorMessage, isAppError } from '../../utils/errorHandler';
import { MIN_PASSWORD_LENGTH, PASSWORD_REQUIREMENT_MESSAGE } from '../../config/validation';
import { validateBirthDate, requiresConsentFlag } from '../../utils/age';
import './Auth.css';
import { useLanguage } from '../../contexts/LanguageContext';

const logger = createComponentLogger('Register');

// Translated 2026-09-05 against the professionally translated dictionaries in
// config/i18nConfig.ts. Only strings with an existing key are keyed: the rest
// keep their English text rather than being machine-translated into six
// languages, because a district product's translations get certified and
// unreviewed strings in front of children are not an improvement on English.
export const Register: React.FC = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // C3 break #1: no age was collected anywhere, so COPPA could not be applied.
  const [birthDate, setBirthDate] = useState('');
  const [birthDateError, setBirthDateError] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState('');
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const validateForm = () => {
    let isValid = true;
    
    // Clear previous errors
    setEmailError('');
    setPasswordError('');
    setConfirmPasswordError('');
    setBirthDateError('');
    setTermsError('');
    setError('');
    
    // Validate email
    if (!email) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address');
      isValid = false;
    }
    
    // #132 fix (2026-05-18): threshold sourced from `src/config/validation.ts`
    // so UI message + test assertions can't drift apart.
    if (!password) {
      setPasswordError('Password is required');
      isValid = false;
    } else if (password.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(PASSWORD_REQUIREMENT_MESSAGE);
      isValid = false;
    } else if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password)) {
      setPasswordError('Password needs an uppercase letter, a lowercase letter, and a number');
      isValid = false;
    }
    
    // Validate confirm password
    if (!confirmPassword) {
      setConfirmPasswordError('Please confirm your password');
      isValid = false;
    } else if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      isValid = false;
    }

    // Date of birth. Being under 13 is NOT an error — it routes the user into
    // the parental-consent flow after the account is created.
    const dobError = validateBirthDate(birthDate);
    if (dobError) {
      setBirthDateError(dobError);
      isValid = false;
    }

    // Validate terms agreement
    if (!agreedToTerms) {
      setTermsError('You must agree to the Terms of Service and Privacy Policy');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form first
    if (!validateForm()) {
      if (!email && !password && !confirmPassword) {
        setError('Please fill in all required fields');
      }
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      await register(email, password, birthDate);
      // Under-13 accounts go straight to the consent handshake; they should
      // not reach the product before a parent has been asked.
      navigate(requiresConsentFlag(birthDate) ? '/consent' : '/dashboard');
    } catch (error: unknown) {
      logger.error('Registration error:', error);
      
      if (isAppError(error)) {
        setError(error.message);
      } else {
        setError(getErrorMessage(error) || 'Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    if (!agreedToTerms) {
      setTermsError('You must agree to the Terms of Service and Privacy Policy');
      return;
    }

    // S98: the date of birth typed on THIS page was not read here and not
    // validated here, so a 9-year-old could fill in their real birth date, click
    // "Sign up with Google", and be provisioned with no age at all — the age they
    // gave was thrown away at the moment it mattered. If they filled it in, carry
    // it to `/accept-terms`, which is the screen that records it; and validate it
    // here, because an invalid date should be corrected before leaving this page
    // rather than silently dropped.
    let carriedBirthDate = '';
    if (birthDate) {
      const problem = validateBirthDate(birthDate);
      if (problem) {
        setBirthDateError(problem);
        return;
      }
      carriedBirthDate = birthDate;
    }

    try {
      setError('');
      setLoading(true);
      // #46 fix (2026-05-18): same ToS-routing as Login.tsx. First-time
      // Google users get routed to /accept-terms instead of straight to
      // the dashboard — captures the consent timestamp the user doc was
      // initialized with `termsAcceptedAt: null` for.
      const result = await loginWithGoogle();

      // S98: this was `if (isNewUser) → /accept-terms; else → /dashboard`, so an
      // UNKNOWN result took the permissive branch. `loginWithGoogle` is typed
      // `Promise<{ isNewUser: boolean } | void>`, and a provisioning path that
      // returns nothing — or a `getAdditionalUserInfo` that came back empty —
      // therefore skipped `/accept-terms` entirely. That is the only place the
      // OAuth path collects a date of birth, so it re-opened the S28/S54 hole from
      // the client side: an account with no age on record, and `requiresConsent()`
      // reads a missing `requiresParentalConsent` as FALSE.
      //
      // Rule 8d: default to the RESTRICTIVE value. Only a result that explicitly
      // says `isNewUser: false` goes straight to the dashboard; anything else is
      // treated as a first sign-in and sent to collect terms and an age.
      // `/accept-terms` is idempotent for an account that already has both.
      const knownReturningUser =
        result !== null &&
        typeof result === 'object' &&
        'isNewUser' in result &&
        result.isNewUser === false;

      if (knownReturningUser) {
        navigate('/dashboard');
      } else {
        // S232: the date of birth travels in navigation STATE, not the URL. In the URL it stayed
        // in the shared Chromebook's history for the next child, and error reports record
        // `window.location.href`.
        if (carriedBirthDate) {
          navigate('/accept-terms?from=google', { state: { birthDate: carriedBirthDate } });
        } else {
          navigate('/accept-terms?from=google');
        }
      }
    } catch (error: unknown) {
      logger.error('Google signup error:', error);
      if (isAppError(error)) {
        setError(error.message);
      } else {
        setError(getErrorMessage(error) || 'Failed to sign up with Google. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-title">Create Account</h1>
        <p className="auth-subtitle">Join NS Gaming Studio to start creating games</p>
        
        {/* S143: every FIELD error is `role="alert"`; this banner — the one that
            carries the reason a signup was refused, "An account with this email
            already exists" — was a bare div with no role and no live region. A
            screen-reader user pressed Sign Up, the button returned from
            "Creating Account…" to "Sign Up", and nothing was spoken: a refused
            registration and a form that was never submitted sounded identical.
            WCAG 2.2 SC 4.1.3, on the sale path's VPAT. */}
        {error && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">{t('form.email')}</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError('');
                if (error) setError('');
              }}
              required
              placeholder="Enter your email"
              className={emailError ? 'error' : ''}
              aria-describedby={emailError ? "email-error" : undefined}
              aria-invalid={!!emailError}
            />
            {emailError && (
              <div id="email-error" className="field-error" role="alert">
                {emailError}
              </div>
            )}
          </div>
          
          {/* Date of birth — COPPA. We need this to know whether an account
              requires verifiable parental consent (16 CFR 312). It is written
              once at signup and is immutable client-side thereafter. */}
          <div className="form-group">
            <label htmlFor="birthDate">Date of Birth</label>
            <input
              id="birthDate"
              type="date"
              value={birthDate}
              max={new Date().toISOString().split('T')[0]}
              onChange={(e) => {
                setBirthDate(e.target.value);
                if (birthDateError) setBirthDateError('');
                if (error) setError('');
              }}
              required
              className={birthDateError ? 'error' : ''}
              aria-describedby={birthDateError ? 'birthDate-error' : 'birthDate-hint'}
              aria-invalid={!!birthDateError}
            />
            <div id="birthDate-hint" className="field-hint">
              We ask so we can protect younger creators. If you're under 13,
              we'll ask a parent for permission before you start.
            </div>
            {birthDateError && (
              <div id="birthDate-error" className="field-error" role="alert">
                {birthDateError}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('form.password')}</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError('');
                if (error) setError('');
              }}
              required
              placeholder="Min 8 chars, uppercase + lowercase + number"
              className={passwordError ? 'error' : ''}
              aria-describedby={passwordError ? "password-error" : undefined}
              aria-invalid={!!passwordError}
            />
            {passwordError && (
              <div id="password-error" className="field-error" role="alert">
                {passwordError}
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">{t('form.confirmPassword')}</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (confirmPasswordError) setConfirmPasswordError('');
                if (error) setError('');
              }}
              required
              placeholder="Confirm your password"
              className={confirmPasswordError ? 'error' : ''}
              aria-describedby={confirmPasswordError ? "confirm-password-error" : undefined}
              aria-invalid={!!confirmPasswordError}
            />
            {confirmPasswordError && (
              <div id="confirm-password-error" className="field-error" role="alert">
                {confirmPasswordError}
              </div>
            )}
          </div>
          
          <div className="form-group terms-group">
            <label className="terms-label">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => {
                  setAgreedToTerms(e.target.checked);
                  if (termsError) setTermsError('');
                }}
                className={termsError ? 'error' : ''}
              />
              <span>
                I agree to the{' '}
                <Link to="/terms" target="_blank">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank">Privacy Policy</Link>.
                {' '}If under 13, a parent or guardian must agree on my behalf.
              </span>
            </label>
            {termsError && (
              <div className="field-error" role="alert">{termsError}</div>
            )}
          </div>

          <button type="submit" className="auth-button" disabled={loading}>
            {loading ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>
        
        <div className="auth-divider">
          <span>OR</span>
        </div>
        
        <button 
          onClick={handleGoogleSignup} 
          className="auth-button google-button"
          disabled={loading}
        >
          <svg className="google-icon" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign up with Google
        </button>
        
        <div className="auth-footer">
          <p>Already have an account? <Link to="/login">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
};